import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Zap, Maximize2, ArrowUpRight, Facebook } from 'lucide-react';
import { KRAFT } from '@/constants/testIds';

export const FACEBOOK_GROUP_URL = 'https://www.facebook.com/groups/660291664606293/';

const ITEMS = [
  {
    id: 'sluzbowy-vehicle',
    src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_elektryk-profi/artifacts/dzq0crg4_485846147_9528620003919197_1305623053337620406_n.webp',
    tag: 'Serwis mobilny',
    code: '01',
    title: 'Firmowy pojazd serwisowy',
    scope: 'Warszawa i okolice',
    body:
      'W pełni wyposażony van serwisowy — komplet mierników, przyrządów pomiarowych i osprzętu do bezpiecznego wykonania prac pod nadzorem SEP oraz zgodnie z procedurami BHP.',
    compliance: 'PN-EN 50110 · BHP · pomiary in-situ',
  },
  {
    id: 'sep-e-cert',
    src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_elektryk-profi/artifacts/zxnuzk8b_481960093_9444071419040723_5093503271794084158_n.webp',
    tag: 'Uprawnienia',
    code: '02',
    title: 'Świadectwa SEP · Eksploatacja',
    scope: 'Komisja Kwalifikacyjna nr 711 · Warszawa',
    body:
      'Ważne świadectwa kwalifikacyjne E1 i E2 uprawniające do eksploatacji urządzeń, instalacji i sieci elektroenergetycznych. Numery: E1/259/711/2022 oraz E2/263/711/2022. Ważność do 22 lutego 2027.',
    compliance: 'SEP · Instytut Energetyki i Chłodnictwa',
  },
  {
    id: 'sep-d-cert',
    src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_elektryk-profi/artifacts/1o8txb0e_481980868_9444071515707380_3595043020754095121_n.webp',
    tag: 'Uprawnienia',
    code: '03',
    title: 'Świadectwa SEP · Dozór',
    scope: 'Komisja Kwalifikacyjna nr 711 · Warszawa',
    body:
      'Ważne świadectwa kwalifikacyjne D1 i D2 uprawniające do dozoru nad eksploatacją urządzeń, instalacji i sieci elektroenergetycznych. Numery: D1/260/711/2022 oraz D2/264/711/2022. Ważność do 22 lutego 2027.',
    compliance: 'SEP · Instytut Energetyki i Chłodnictwa',
  },
  {
    id: 'rozdzielnica-ip65',
    src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_elektryk-profi/artifacts/mbfxc4cy_706397320_27297301833291074_8012854931189621772_n.jpg',
    tag: 'Rozdzielnica',
    code: '04',
    title: 'Rozdzielnica zewnętrzna IP65 · 1000 V DC',
    scope: 'Obiekt komercyjny · przyłącze wielotorowe',
    body:
      'Kompletna rozdzielnica w obudowie IP65 z zabezpieczeniami nadprądowymi, wyłącznikiem różnicowoprądowym RCD oraz gniazdem serwisowym. Certyfikowane komponenty IDEAL z oznaczeniami CE — pełny protokół pomiarowy dołączony do dokumentacji.',
    compliance: 'PN-IEC 60364 · IP65 · RCD 30 mA · CE',
  },
  {
    id: 'pompa-ciepla',
    src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_elektryk-profi/artifacts/dwome0k0_706318174_27297301556624435_528469144551752578_n.jpg',
    tag: 'Automatyka techniczna',
    code: '05',
    title: 'Instalacja pompy ciepła HYDRO-Pro',
    scope: 'Pomieszczenie techniczne · niecka basenowa',
    body:
      'Kompleksowe podpięcie pompy ciepła Hydro-Pro z filtrem piaskowym, zaworami sekcyjnymi i sterowaniem elektronicznym. Trasa niskoprądowa i zasilanie 230 V rozprowadzone z zabezpieczeniami RCD w oddzielnej rozdzielni.',
    compliance: 'DTR producenta · RCD · pomiary uruchomieniowe',
  },
  {
    id: 'firma-godna-zaufania',
    src: 'https://customer-assets-wrfwihn1.emergentagent.net/job_elektryk-profi/artifacts/ow5v0e68_542034210_24621065987581352_420287753040421846_n.jpg',
    tag: 'Certyfikat',
    code: '06',
    title: 'Certyfikat "Firma Godna Zaufania"',
    scope: 'Kraft-Performance · rok 2025',
    body:
      'Certyfikat "Firma Godna Zaufania" przyznany za nieposzlakowaną opinię, etykę w prowadzeniu działalności biznesowej oraz rzetelne podejście do zobowiązań wobec klientów i kontrahentów. Weryfikacja niezależna, program branżowy.',
    compliance: 'Program certyfikacji · weryfikacja publiczna',
  },
];

export default function Gallery() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setActive(null);
      if (e.key === 'ArrowLeft') setActive((i) => (i > 0 ? i - 1 : ITEMS.length - 1));
      if (e.key === 'ArrowRight') setActive((i) => (i < ITEMS.length - 1 ? i + 1 : 0));
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [active]);

  const openAt = (i) => setActive(i);
  const close = () => setActive(null);
  const prev = () => setActive((i) => (i > 0 ? i - 1 : ITEMS.length - 1));
  const next = () => setActive((i) => (i < ITEMS.length - 1 ? i + 1 : 0));

  return (
    <section
      id="gallery"
      data-testid={KRAFT.galleryRoot}
      className="relative bg-ink-950 py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-volt" />
              <span className="eyebrow">Realizacje · Portfolio</span>
            </div>
            <h2 className="font-display uppercase text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-4xl">
              Prace, które <span className="text-volt italic font-normal">przechodzą odbiór</span> za pierwszym razem.
            </h2>
          </div>
          <p className="max-w-md text-slate-300 leading-relaxed">
            Zdjęcia z realnych zleceń — kluczowe węzły techniczne, rozdzielacze, rozprowadzenia i osprzęt.
            Każdy projekt kończy się kompletem dokumentacji odbiorowej.
          </p>
        </div>

        {/* Asymmetric bento gallery */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-px bg-white/10">
          {ITEMS.map((item, i) => {
            const span = 'md:col-span-3';
            return (
              <motion.button
                key={item.id}
                data-testid={KRAFT.galleryItem(item.id)}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => openAt(i)}
                className={`group relative bg-ink-950 overflow-hidden ${span} aspect-[16/10] md:aspect-auto md:min-h-[320px]`}
              >
                {/* Image */}
                <div className="absolute inset-0">
                  <img
                    src={item.src}
                    alt={`${item.title} — Kraft-Performance, elektryk Warszawa. ${item.scope}.`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                  />
                </div>
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-transparent" />
                {/* Grain */}
                <div className="grain-overlay opacity-10 group-hover:opacity-20 transition-opacity" />

                {/* Top-left tag */}
                <div className="absolute top-5 left-5 flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] bg-volt text-ink-950 px-2 py-1">
                    {item.code}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/80 bg-ink-950/60 backdrop-blur-sm px-2 py-1 border border-white/10">
                    {item.tag}
                  </span>
                </div>

                {/* Top-right expand icon */}
                <div className="absolute top-5 right-5 w-9 h-9 border border-white/20 flex items-center justify-center bg-ink-950/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 className="w-4 h-4 text-white" />
                </div>

                {/* Bottom text */}
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 text-left">
                  <h3 className="font-display uppercase text-white text-xl md:text-2xl leading-tight tracking-tight mb-2 max-w-[80%]">
                    {item.title}
                  </h3>
                  <div className="flex items-center gap-2 text-slate-400 text-xs mb-3">
                    <Zap className="w-3 h-3 text-volt" />
                    <span>{item.scope}</span>
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-volt/90">
                    {item.compliance}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>

        <motion.a
          href={FACEBOOK_GROUP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={KRAFT.galleryFacebookCta}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 relative block bg-ink-900 border border-white/10 hover:border-volt transition-colors duration-500 group overflow-hidden"
        >
          {/* subtle animated stripe */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-volt to-transparent opacity-40 group-hover:opacity-100 transition-opacity" />
          <div className="p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 border border-volt bg-volt/10 flex items-center justify-center shrink-0 group-hover:bg-volt transition-colors">
                <Facebook className="w-6 h-6 text-volt group-hover:text-ink-950 transition-colors" strokeWidth={2} />
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-volt mb-1">
                  Grupa branżowa · Facebook
                </div>
                <div className="font-display uppercase text-white text-2xl md:text-3xl tracking-tight leading-tight">
                  Zobacz więcej realizacji <span className="text-volt italic font-normal">na żywo</span>
                </div>
                <p className="text-slate-400 text-sm mt-2 max-w-xl">
                  Codzienna dawka zdjęć z placu, filmy z realizacji, pytania i odpowiedzi.
                  Publikujemy tam pełne portfolio — dołącz i śledź naszą pracę.
                </p>
              </div>
            </div>
            <div className="btn-volt whitespace-nowrap self-start md:self-center">
              Dołącz do grupy
              <ArrowUpRight className="w-4 h-4" />
            </div>
          </div>
        </motion.a>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid={KRAFT.galleryLightbox}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-ink-950/95 backdrop-blur-md flex items-center justify-center p-4"
            onClick={close}
          >
            <button
              data-testid={KRAFT.galleryLightboxClose}
              onClick={close}
              className="absolute top-6 right-6 w-11 h-11 border border-white/20 flex items-center justify-center text-white hover:border-volt hover:text-volt transition-colors"
              aria-label="Zamknij"
            >
              <X className="w-5 h-5" />
            </button>
            <button
              data-testid={KRAFT.galleryLightboxPrev}
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 border border-white/20 flex items-center justify-center text-white hover:border-volt hover:text-volt transition-colors"
              aria-label="Poprzednie"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              data-testid={KRAFT.galleryLightboxNext}
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 border border-white/20 flex items-center justify-center text-white hover:border-volt hover:text-volt transition-colors"
              aria-label="Następne"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="lg:col-span-8 bg-ink-950">
                <img
                  src={ITEMS[active].src}
                  alt={`${ITEMS[active].title} — Kraft-Performance, elektryk Warszawa. Realizacja: ${ITEMS[active].scope}.`}
                  className="w-full h-full object-contain max-h-[80vh] bg-ink-950"
                />
              </div>
              <div className="lg:col-span-4 bg-ink-900 p-8">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] bg-volt text-ink-950 px-2 py-1">
                    {ITEMS[active].code}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                    {ITEMS[active].tag}
                  </span>
                </div>
                <h3 className="font-display uppercase text-white text-2xl leading-tight tracking-tight">
                  {ITEMS[active].title}
                </h3>
                <div className="flex items-center gap-2 mt-3 text-slate-400 text-sm">
                  <Zap className="w-4 h-4 text-volt" />
                  <span>{ITEMS[active].scope}</span>
                </div>
                <p className="mt-6 text-slate-300 text-sm leading-relaxed">
                  {ITEMS[active].body}
                </p>
                <div className="mt-8 pt-6 border-t border-white/10">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2">
                    Zgodność
                  </div>
                  <div className="font-mono text-xs uppercase tracking-wider text-volt">
                    {ITEMS[active].compliance}
                  </div>
                </div>
                <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  {active + 1} / {ITEMS.length}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
